import React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { i18n } from "../../../translate/i18n";

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 12,
  fontSize: 12,
};

const cellStyle = {
  border: "1px solid #ddd",
  padding: "6px 8px",
};

const headerCellStyle = {
  ...cellStyle,
  background: "#f0f3f9",
  fontWeight: 700,
};

const rightCellStyle = {
  ...cellStyle,
  textAlign: "center",
};

const rightHeaderCellStyle = {
  ...headerCellStyle,
  textAlign: "center",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    return format(new Date(`${dateString}T00:00:00`), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "-";
  }
};

const KanbanCardsReportPrintTemplate = React.forwardRef(({ reportData }, ref) => {
  const generatedAt = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });

  if (!reportData) {
    return <div ref={ref}>{i18n.t("usageReport.print.noData")}</div>;
  }

  const {
    period,
    createdStats = [],
    forwardedToOthers = [],
  } = reportData;

  const typeLabel = (type) => {
    if (type === "move") return i18n.t("usageReport.forwarding.types.move");
    if (type === "copy") return i18n.t("usageReport.forwarding.types.copy");
    return i18n.t("usageReport.forwarding.types.create");
  };

  // Agrupa por (Origem, Destino) para mesclar essas colunas e listar os tipos dentro do grupo
  const forwardedGroups = [];
  const forwardedGroupIndex = {};
  for (const row of forwardedToOthers) {
    const key = `${row.senderId}|${row.receiverId}`;
    if (forwardedGroupIndex[key] === undefined) {
      forwardedGroupIndex[key] = forwardedGroups.length;
      forwardedGroups.push({
        senderName: row.senderName,
        receiverName: row.receiverName,
        items: [],
      });
    }
    forwardedGroups[forwardedGroupIndex[key]].items.push({
      type: row.type,
      count: row.count,
    });
  }

  const mergedCellStyle = { ...cellStyle, verticalAlign: "middle" };
  const groupStartCellStyle = { ...cellStyle, borderTop: "2px solid #bbb" };
  const groupStartMergedStyle = { ...mergedCellStyle, borderTop: "2px solid #bbb" };

  return (
    <div ref={ref} style={{ padding: 16, color: "#111", fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ margin: 0 }}>{i18n.t("usageReport.forwarding.reportTitle")}</h2>
      <p style={{ margin: "6px 0 0 0", fontSize: 12 }}>
        {i18n.t("usageReport.periodLabel", {
          period: i18n.t("usageReport.periodRange", { from: formatDate(period?.dateFrom), to: formatDate(period?.dateTo) }),
        })}
      </p>
      <p style={{ margin: "2px 0 0 0", fontSize: 12 }}>{i18n.t("usageReport.generatedAt", { date: generatedAt })}</p>

      {/* Seção: Criação */}
      <h3 style={{ marginTop: 20, marginBottom: 4, fontSize: 13 }}>{i18n.t("usageReport.forwarding.createdTitle")}</h3>
      {createdStats.length === 0 ? (
        <p style={{ fontSize: 12, color: "#666" }}>{i18n.t("usageReport.forwarding.createdEmpty")}</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>{i18n.t("usageReport.columns.user")}</th>
              <th style={rightHeaderCellStyle}>{i18n.t("usageReport.forwarding.cardsCreated")}</th>
            </tr>
          </thead>
          <tbody>
            {createdStats.map((row) => (
              <tr key={row.userId}>
                <td style={cellStyle}>{row.userName}</td>
                <td style={rightCellStyle}>{row.cardsCreated}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...headerCellStyle, fontWeight: 700 }}>{i18n.t("common.total")}</td>
              <td style={rightHeaderCellStyle}>
                {createdStats.reduce((acc, r) => acc + r.cardsCreated, 0)}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Seção: Cards Encaminhados a Outros Usuários (criação / cópia / movimentação) */}
      <h3 style={{ marginTop: 24, marginBottom: 4, fontSize: 13 }}>{i18n.t("usageReport.forwarding.forwardedTitle")}</h3>
      {forwardedToOthers.length === 0 ? (
        <p style={{ fontSize: 12, color: "#666" }}>{i18n.t("usageReport.forwarding.forwardedEmpty")}</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>{i18n.t("usageReport.forwarding.origin")}</th>
              <th style={headerCellStyle}>{i18n.t("usageReport.forwarding.destination")}</th>
              <th style={headerCellStyle}>{i18n.t("common.type")}</th>
              <th style={rightHeaderCellStyle}>{i18n.t("usageReport.forwarding.quantity")}</th>
            </tr>
          </thead>
          <tbody>
            {forwardedGroups.map((group, gIdx) =>
              group.items.map((item, iIdx) => (
                <tr key={`${gIdx}-${iIdx}`}>
                  {iIdx === 0 && (
                    <>
                      <td
                        style={gIdx === 0 ? mergedCellStyle : groupStartMergedStyle}
                        rowSpan={group.items.length}
                      >
                        {group.senderName}
                      </td>
                      <td
                        style={gIdx === 0 ? mergedCellStyle : groupStartMergedStyle}
                        rowSpan={group.items.length}
                      >
                        {group.receiverName}
                      </td>
                    </>
                  )}
                  <td style={gIdx !== 0 && iIdx === 0 ? groupStartCellStyle : cellStyle}>
                    {typeLabel(item.type)}
                  </td>
                  <td
                    style={
                      gIdx !== 0 && iIdx === 0
                        ? { ...rightCellStyle, borderTop: "2px solid #bbb" }
                        : rightCellStyle
                    }
                  >
                    {item.count}
                  </td>
                </tr>
              ))
            )}
            <tr>
              <td style={{ ...headerCellStyle, fontWeight: 700 }} colSpan={3}>{i18n.t("common.total")}</td>
              <td style={rightHeaderCellStyle}>
                {forwardedToOthers.reduce((acc, r) => acc + r.count, 0)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
});

KanbanCardsReportPrintTemplate.displayName = "KanbanCardsReportPrintTemplate";

export default KanbanCardsReportPrintTemplate;
